import { useState, useEffect } from 'react'
import api from '../../utils/api'
import AdminLayout from '../../components/admin/AdminLayout'
import './AdminLegacyCompletions.css'

interface LegacyCompletion {
  id: number
  user_id: number
  name: string
  birth_date: string
  organization: string
  year: number
  season: string
  course_title: string
  grade: string
  credits: number
  completion_date: string | null
  created_at: string
  email: string
  user_name: string
}

export default function AdminLegacyCompletions() {
  const [completions, setCompletions] = useState<LegacyCompletion[]>([])
  const [loading, setLoading] = useState(true)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<any>(null)
  const [filterYear, setFilterYear] = useState<string>('')
  const [filterSeason, setFilterSeason] = useState<string>('')
  const [filterEmail, setFilterEmail] = useState<string>('')

  const fetchCompletions = async () => {
    try {
      setLoading(true)
      const params: any = {}
      
      if (filterYear) params.year = filterYear
      if (filterSeason) params.season = filterSeason
      
      const response = await api.get('/legacy-completions', { params })
      
      let data = response.data
      
      // 이메일 필터링 (클라이언트 사이드)
      if (filterEmail) {
        data = data.filter((c: LegacyCompletion) => 
          c.email.toLowerCase().includes(filterEmail.toLowerCase())
        )
      }
      
      setCompletions(data)
    } catch (error) {
      console.error('이전 이수 내역 조회 오류:', error)
      alert('이전 이수 내역을 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCompletions()
  }, [filterYear, filterSeason])

  useEffect(() => {
    // 이메일 필터는 디바운스 적용
    const timer = setTimeout(() => {
      if (filterEmail !== undefined) {
        fetchCompletions()
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [filterEmail])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0])
      setUploadResult(null)
    }
  }

  const handleUpload = async () => {
    if (!uploadFile) {
      alert('파일을 선택해주세요.')
      return
    }

    try {
      setUploading(true)
      setUploadResult(null)
      
      const formData = new FormData()
      formData.append('file', uploadFile)
      
      const response = await api.post('/legacy-completions/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      setUploadResult(response.data)
      setUploadFile(null)
      
      // 파일 입력 초기화
      const fileInput = document.getElementById('file-upload') as HTMLInputElement
      if (fileInput) fileInput.value = ''
      
      // 목록 새로고침
      fetchCompletions()
      
      alert(`업로드 완료!\n성공: ${response.data.successCount}건\n실패: ${response.data.errorCount}건`)
    } catch (error: any) {
      console.error('업로드 오류:', error)
      alert(error.response?.data?.error || '업로드에 실패했습니다.')
    } finally {
      setUploading(false)
    }
  }

  const handleDownloadSample = async () => {
    try {
      const response = await api.get('/legacy-completions/sample', {
        responseType: 'blob'
      })
      
      // Blob을 다운로드 링크로 변환
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'legacy_sample.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      console.error('샘플 다운로드 오류:', error)
      // Blob 응답이 JSON 에러인 경우 처리
      if (error.response?.data instanceof Blob) {
        const reader = new FileReader()
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result as string)
            alert(errorData.error || '샘플 파일 다운로드에 실패했습니다.')
          } catch {
            alert('샘플 파일 다운로드에 실패했습니다.')
          }
        }
        reader.readAsText(error.response.data)
      } else {
        alert(error.response?.data?.error || '샘플 파일 다운로드에 실패했습니다.')
      }
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('정말 삭제하시겠습니까?')) {
      return
    }

    try {
      await api.delete(`/legacy-completions/${id}`)
      
      alert('삭제되었습니다.')
      fetchCompletions()
    } catch (error) {
      console.error('삭제 오류:', error)
      alert('삭제에 실패했습니다.')
    }
  }

  const handleResetFilters = () => {
    setFilterYear('')
    setFilterSeason('')
    setFilterEmail('')
  }

  return (
    <AdminLayout>
      <div className="admin-legacy-completions">
        <h1>📜 이전 이수 내역 관리</h1>
        
        {/* 업로드 섹션 */}
        <div className="upload-section">
          <h2>일괄 업로드</h2>
          <div className="upload-controls">
            <button onClick={handleDownloadSample} className="btn btn-secondary">
              📥 엑셀 샘플 다운로드
            </button>
            <div className="file-upload-group">
              <input
                id="file-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={uploading}
              />
              <button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className="btn btn-primary"
              >
                {uploading ? '업로드 중...' : '📤 업로드'}
              </button>
            </div>
          </div>
          
          {uploadResult && (
            <div className={`upload-result ${uploadResult.errorCount > 0 ? 'has-errors' : ''}`}>
              <h3>업로드 결과</h3>
              <p>총 {uploadResult.totalRows}건 중 성공 {uploadResult.successCount}건, 실패 {uploadResult.errorCount}건</p>
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <details>
                  <summary>오류 상세 ({uploadResult.errors.length}건)</summary>
                  <ul>
                    {uploadResult.errors.map((error: string, index: number) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          
          <div className="upload-info">
            <h3>업로드 양식 안내</h3>
            <p>엑셀 파일은 다음 컬럼을 포함해야 합니다:</p>
            <ul>
              <li><strong>이메일</strong> (필수): 시스템에 등록된 사용자 이메일</li>
              <li><strong>성명</strong> (필수): 학생 이름</li>
              <li><strong>생년월일</strong> (선택): YYYY-MM-DD 형식</li>
              <li><strong>소속기관</strong> (선택): 교회/기관명</li>
              <li><strong>년도</strong> (필수): 취득년도 (예: 2023)</li>
              <li><strong>학기</strong> (필수): "하기" 또는 "동기"</li>
              <li><strong>과목명</strong> (필수): 강의명</li>
              <li><strong>성적</strong> (필수): A, B+, B, C 등</li>
              <li><strong>학점</strong> (필수): 숫자 (예: 3)</li>
              <li><strong>이수일자</strong> (선택): YYYY-MM-DD 형식</li>
            </ul>
          </div>
        </div>

        {/* 필터 섹션 */}
        <div className="filters">
          <h2>필터</h2>
          <div className="filter-group">
            <div className="filter-item">
              <label>년도:</label>
              <input
                type="number"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                placeholder="전체"
                min="2000"
                max="2099"
              />
            </div>
            <div className="filter-item">
              <label>학기:</label>
              <select value={filterSeason} onChange={(e) => setFilterSeason(e.target.value)}>
                <option value="">전체</option>
                <option value="하기">하기</option>
                <option value="동기">동기</option>
              </select>
            </div>
            <div className="filter-item">
              <label>이메일:</label>
              <input
                type="text"
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value)}
                placeholder="이메일 검색"
              />
            </div>
            <button onClick={handleResetFilters} className="btn btn-secondary">
              🔄 필터 초기화
            </button>
          </div>
        </div>

        {/* 목록 섹션 */}
        <div className="list-section">
          <h2>이전 이수 내역 목록 ({completions.length}건)</h2>
          
          {loading ? (
            <p>로딩 중...</p>
          ) : completions.length === 0 ? (
            <p>등록된 이전 이수 내역이 없습니다.</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>번호</th>
                    <th>이메일</th>
                    <th>성명</th>
                    <th>소속기관</th>
                    <th>취득년도</th>
                    <th>과목명</th>
                    <th>성적</th>
                    <th>학점</th>
                    <th>이수일자</th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {completions.map((completion, index) => (
                    <tr key={completion.id}>
                      <td>{index + 1}</td>
                      <td>{completion.email}</td>
                      <td>{completion.name}</td>
                      <td>{completion.organization || '-'}</td>
                      <td>{completion.year}년 {completion.season}</td>
                      <td>{completion.course_title}</td>
                      <td>{completion.grade}</td>
                      <td>{completion.credits}</td>
                      <td>{completion.completion_date || '-'}</td>
                      <td>
                        <button
                          onClick={() => handleDelete(completion.id)}
                          className="btn btn-danger btn-sm"
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}


